import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import LocationsSection from "@/components/LocationsSection";
import { toast } from "sonner";
import { UploadCloud, CheckCircle2, ArrowLeft, ShoppingBag, Download } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

const API = process.env.REACT_APP_BACKEND_URL;
const POSITION_TITLES = ["Hair/Salon Stylist", "Braider/Loctition", "Master Groomer/Barber", "Owner", "Manager", "Other"];

const EMPTY = {
  name: "", company: "", email: "", phone: "", address: "", zip_code: "",
  birthday: "", anniversary: "", notes: "",
  has_multiple_locations: false, additional_locations: [],
  license_number: "", license_issuing_state: "", license_expires_at: "",
  position_title: "", other_position_title: "",
};

export default function PublicClientApply() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [licenseFile, setLicenseFile] = useState(null);
  const [licensePreview, setLicensePreview] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [welcome, setWelcome] = useState(null); // {slug, sub_store_path, photo_url}
  // Self-enroll "update existing" flow (matched by phone)
  const [matchClient, setMatchClient] = useState(null); // lookup result awaiting confirm
  const [updateMode, setUpdateMode] = useState(false);
  const [updateClientId, setUpdateClientId] = useState("");
  const [existingPhotoUrl, setExistingPhotoUrl] = useState("");
  const [existingLicenseUrl, setExistingLicenseUrl] = useState("");
  const [dismissedKeys, setDismissedKeys] = useState([]);
  const qrRef = useRef(null);
  const fileRef = useRef(null);
  const licenseRef = useRef(null);

  useEffect(() => {
    // Self-enrolled clients always default to Warehouse as their upline;
    // no referral selection is exposed on the public apply form.
  }, []);

  const digitsOnly = (p) => (p || "").replace(/\D/g, "");
  const phoneKey = (p) => { const d = digitsOnly(p); return d.length >= 10 ? d.slice(-10) : d; };

  // Detect an existing self-enrolled profile by phone (last 10 digits) and offer to update it.
  useEffect(() => {
    if (updateMode) return;
    const key = phoneKey(form.phone);
    if (key.length < 10 || dismissedKeys.includes(key)) { setMatchClient(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/api/clients/apply/lookup`, { params: { phone: form.phone } });
        if (data.found) setMatchClient(data.client); else setMatchClient(null);
      } catch { setMatchClient(null); }
    }, 600);
    return () => clearTimeout(t);
  }, [form.phone, updateMode, dismissedKeys]);

  const applyPrefill = (c) => {
    setForm({
      name: c.name || "", company: c.company || "", email: c.email || "",
      phone: c.phone || form.phone, address: c.address || "", zip_code: c.zip_code || "",
      birthday: c.birthday || "", anniversary: c.anniversary || "", notes: c.notes || "",
      has_multiple_locations: !!c.has_multiple_locations, additional_locations: c.additional_locations || [],
      license_number: c.license_number || "",
      license_issuing_state: c.license_issuing_state || "", license_expires_at: c.license_expires_at || "",
      position_title: c.position_title || "", other_position_title: c.other_position_title || "",
    });
    setUpdateMode(true);
    setUpdateClientId(c.id);
    setExistingPhotoUrl(c.photo_url || "");
    setExistingLicenseUrl(c.license_image_url || "");
    setMatchClient(null);
    toast.success("Profile loaded — update any details and save");
  };

  const dismissMatch = () => {
    setDismissedKeys((k) => [...k, phoneKey(form.phone)]);
    setMatchClient(null);
  };

  const handlePhoto = (file) => {
    if (!file) return;
    if (!/\.(jpe?g|png|gif|webp)$/i.test(file.name)) { toast.error("Photo must be JPG, PNG, GIF, or WebP"); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("Photo must be under 15 MB"); return; }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleLicense = (file) => {
    if (!file) return;
    if (!/\.(jpe?g|png|gif|webp)$/i.test(file.name)) { toast.error("License image must be JPG, PNG, GIF, or WebP"); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("License image must be under 15 MB"); return; }
    setLicenseFile(file);
    setLicensePreview(URL.createObjectURL(file));
  };

  const submit = async (e) => {
    e.preventDefault();
    // client-side mandatory checks
    const missing = [];
    if (!form.name.trim()) missing.push("Name");
    if (!form.company.trim()) missing.push("Company");
    if (!form.email.trim()) missing.push("Email");
    if (!form.phone.trim()) missing.push("Phone");
    if (!form.address.trim()) missing.push("Address");
    if (!form.zip_code.trim()) missing.push("Zip code");
    if (!photoFile && !existingPhotoUrl) missing.push("Professional Photo Upload");
    if (!form.birthday) missing.push("Birthday");
    if (!form.license_number.trim()) missing.push("Cosmetology License Number");
    if (!form.license_issuing_state) missing.push("License Issuing State");
    if (!form.license_expires_at) missing.push("License Expiration Date");
    if (!licenseFile && !existingLicenseUrl) missing.push("Cosmetology License Image");
    if (missing.length) { toast.error(`Please complete: ${missing.join(", ")}`); return; }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("company", form.company.trim());
      fd.append("email", form.email.trim());
      fd.append("phone", form.phone.trim());
      fd.append("address", form.address.trim());
      fd.append("zip_code", form.zip_code.trim());
      fd.append("birthday", form.birthday);
      fd.append("anniversary", form.anniversary || "");
      fd.append("notes", form.notes || "");
      // Self-enrolled clients are always assigned to the Warehouse upline.
      // Referral selection is handled exclusively via the admin back-office.
      fd.append("referred_by_partner_id", "warehouse");
      fd.append("referred_by_other", "");
      fd.append("has_multiple_locations", form.has_multiple_locations ? "true" : "false");
      fd.append("additional_locations", JSON.stringify(form.additional_locations || []));
      if (updateMode) fd.append("client_id", updateClientId);
      if (photoFile) fd.append("photo", photoFile);
      fd.append("license_number", form.license_number.trim());
      fd.append("license_issuing_state", form.license_issuing_state);
      fd.append("license_expires_at", form.license_expires_at);
      fd.append("position_title", form.position_title);
      fd.append("other_position_title", form.other_position_title);
      if (licenseFile) fd.append("license_image", licenseFile);
      const { data } = await axios.post(`${API}/api/clients/apply`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setWelcome({
        slug: data.slug,
        sub_store_path: data.sub_store_path,
        photo_url: data.photo_url,
        updated: !!data.updated,
      });
      setSubmitted(true);
      toast.success(data.updated ? "Profile updated!" : "Welcome to MPIRE!");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted && welcome) {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const subStoreUrl = `${origin}${welcome.sub_store_path}`;
    const photoSrc = welcome.photo_url ? `${API}${welcome.photo_url}` : "";

    const downloadQr = () => {
      const svg = qrRef.current?.querySelector("svg");
      if (!svg) return;
      const xml = new XMLSerializer().serializeToString(svg);
      const svg64 = btoa(unescape(encodeURIComponent(xml)));
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement("canvas");
        const size = 900;
        canvas.width = size; canvas.height = size;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, size, size);
        ctx.drawImage(image, 0, 0, size, size);
        const png = canvas.toDataURL("image/png");
        const a = document.createElement("a");
        a.href = png;
        a.download = `${welcome.slug}-substore-qr.png`;
        a.click();
      };
      image.src = `data:image/svg+xml;base64,${svg64}`;
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-[var(--brand)]/10 via-white to-white flex items-center justify-center p-4">
        <Card className="max-w-lg w-full p-6 sm:p-8 text-center space-y-5" data-testid="apply-welcome">
          <div className="flex flex-col items-center gap-3">
            {photoSrc && (
              <img
                src={photoSrc}
                alt={form.name}
                className="w-24 h-24 rounded-full object-cover border-4 border-[var(--brand)]/50 shadow-md"
                data-testid="welcome-photo"
              />
            )}
            <div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium mb-2">
                <CheckCircle2 size={12} /> {welcome.updated ? "Profile updated" : "Welcome, you're in"}
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[var(--ink)]">
                {welcome.updated
                  ? `${form.name?.split(" ")[0]}, your profile is updated`
                  : `${form.name?.split(" ")[0]}, your sub-store is live`}
              </h1>
              <p className="text-sm text-gray-600 mt-1.5 leading-snug">
                Save this QR and share it with your customers — every scan sends them straight to your MPIRE sub-store with your exclusive deals.
              </p>
            </div>
          </div>

          <div ref={qrRef} className="flex justify-center">
            <div className="rounded-2xl border-2 border-[var(--brand)]/30 bg-white p-4 shadow-sm" data-testid="welcome-qr">
              <QRCodeSVG value={subStoreUrl} size={240} level="M" bgColor="#ffffff" fgColor="#B01E24" />
            </div>
          </div>

          <Button className="btn-brand w-full" onClick={downloadQr} data-testid="welcome-download-qr">
            <Download size={16} className="mr-2" /> Save QR to your phone
          </Button>

          <p className="text-[11px] text-gray-500">
            Long-press the QR to save it, or tap the button above. Your MPIRE contact has your sub-store on file.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-soft)]">
      <header className="bg-white border-b border-[var(--border)] px-4 py-3 flex items-center justify-between">
        <Link to="/login" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[var(--brand)]" data-testid="apply-back-link">
          <ArrowLeft size={14} /> Back
        </Link>
        <Link to="/" className="flex items-center gap-2" data-testid="apply-logo-link">
          <img src="/brand/mpire-logo.png" alt="MPIRE Distribution" className="w-8 h-8 object-contain" />
          <span className="font-bold text-sm tracking-tight">MPIRE Distribution</span>
        </Link>
        <div className="w-16" />
      </header>

      <div className="max-w-2xl mx-auto p-4 sm:p-8">
        <div className="mb-6">
          <p className="label-eyebrow text-[var(--brand)]">Become a client</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1 text-[var(--ink)]">Apply for wholesale access</h1>
          <p className="text-gray-600 mt-2 text-sm">
            Tell us about your business. Once approved, you'll get access to wholesale pricing, dedicated support,
            and a private client portal. Fields marked * are required.
          </p>
        </div>

        <Card className="p-4 sm:p-6">
          {matchClient && !updateMode && (
            <div className="mb-4 rounded-lg border border-[var(--brand)]/40 bg-[var(--brand)]/5 p-4" data-testid="apply-match-banner">
              <p className="text-sm font-semibold text-[var(--ink)]">We found a profile for this phone number</p>
              <p className="text-xs text-gray-600 mt-0.5">
                A profile for <span className="font-medium capitalize">{matchClient.name}</span>{matchClient.company ? ` · ${matchClient.company}` : ""} already exists. Update it instead of creating a new one?
              </p>
              <div className="flex gap-2 mt-3">
                <Button type="button" size="sm" className="btn-brand" onClick={() => applyPrefill(matchClient)} data-testid="apply-match-update">
                  Yes, update my profile
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={dismissMatch} data-testid="apply-match-dismiss">
                  No, create new
                </Button>
              </div>
            </div>
          )}
          {updateMode && (
            <div className="mb-4 rounded-lg border border-emerald-300 bg-emerald-50 p-3 flex items-center gap-2" data-testid="apply-update-mode-banner">
              <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
              <p className="text-xs text-emerald-800">You're updating your existing profile. Change any details below and save — no duplicate will be created.</p>
            </div>
          )}
          <form onSubmit={submit} className="space-y-4" data-testid="public-apply-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Name *</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="apply-name" />
              </div>
              <div>
                <Label>Company *</Label>
                <Input required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="apply-company" />
              </div>
              <div className="sm:col-span-2">
                <Label>Position Title</Label>
                <select value={form.position_title} onChange={(e) => setForm({ ...form, position_title: e.target.value })} className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="apply-position-title">
                  <option value="">Select title</option>
                  {POSITION_TITLES.map((title) => <option key={title} value={title}>{title}</option>)}
                </select>
                {form.position_title === "Other" && <Input className="mt-2" value={form.other_position_title} onChange={(e) => setForm({ ...form, other_position_title: e.target.value })} placeholder="Enter a title" data-testid="apply-other-position-title" />}
              </div>

              <div>
                <Label>Email *</Label>
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="apply-email" />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="apply-phone" />
              </div>

              <div className="sm:col-span-2">
                <Label>Address *</Label>
                <Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="apply-address" />
              </div>
              <div>
                <Label>Zip code *</Label>
                <Input required value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} data-testid="apply-zip" />
              </div>
              <div>
                <Label>Birthday *</Label>
                <Input required type="date" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} data-testid="apply-birthday" />
              </div>

              <div className="sm:col-span-2">
                <Label>Professional Photo Upload {existingPhotoUrl ? "" : "*"}</Label>
                <div className="mt-1 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="flex items-center gap-2 px-4 py-2 border border-dashed border-[var(--border)] rounded-md hover:border-[var(--brand)] hover:bg-[var(--brand)]/5 transition-colors text-sm"
                    data-testid="apply-photo-button"
                  >
                    <UploadCloud size={16} className="text-[var(--brand)]" />
                    {photoFile || existingPhotoUrl ? "Change photo" : "Choose photo"}
                  </button>
                  {(photoPreview || existingPhotoUrl) && (
                    <img src={photoPreview || `${API}${existingPhotoUrl}`} alt="Preview" className="w-14 h-14 rounded-full object-cover border-2 border-[var(--brand)]/30" data-testid="apply-photo-preview" />
                  )}
                  {photoFile && <span className="text-xs text-gray-500 truncate max-w-[160px]">{photoFile.name}</span>}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  onChange={(e) => handlePhoto(e.target.files?.[0])}
                  data-testid="apply-photo-input"
                />
                <p className="text-xs text-gray-500 mt-1">{existingPhotoUrl && !photoFile ? "Current photo on file — upload to replace (optional)." : "JPG, PNG, GIF, or WebP · max 15 MB"}</p>
              </div>

              <div className="sm:col-span-2 rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-3" data-testid="apply-license-section">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-amber-800">Cosmetology License · Required</p>
                  <p className="text-[11px] text-amber-800/80 mt-0.5">A state-issued cosmetology license number and a clear photo of the physical license are required. Your account stays in pending review until HQ verifies your license — wholesale pricing unlocks after approval.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>License number *</Label>
                    <Input
                      required
                      value={form.license_number}
                      onChange={(e) => setForm({ ...form, license_number: e.target.value })}
                      placeholder="e.g. TX-COS-123456"
                      data-testid="apply-license-number"
                    />
                  </div>
                  <div><Label>Issuing state *</Label><select value={form.license_issuing_state} onChange={(e) => setForm({ ...form, license_issuing_state: e.target.value })} className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="apply-license-state"><option value="">Select state</option>{["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"].map((state) => <option key={state} value={state}>{state}</option>)}</select></div>
                  <div><Label>License expiration date *</Label><Input type="date" value={form.license_expires_at} onChange={(e) => setForm({ ...form, license_expires_at: e.target.value })} data-testid="apply-license-expiration" /></div>
                  <div>
                    <Label>License photo {existingLicenseUrl ? "" : "*"}</Label>
                    <div className="mt-1 flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => licenseRef.current?.click()}
                        className="flex items-center gap-2 px-4 py-2 border border-dashed border-amber-400 rounded-md hover:border-amber-600 hover:bg-amber-100/70 transition-colors text-sm"
                        data-testid="apply-license-button"
                      >
                        <UploadCloud size={16} className="text-amber-700" />
                        {licenseFile || existingLicenseUrl ? "Change" : "Choose file"}
                      </button>
                      {(licensePreview || existingLicenseUrl) && (
                        <img src={licensePreview || `${API}${existingLicenseUrl}`} alt="License preview" className="w-14 h-14 rounded object-cover border-2 border-amber-400" data-testid="apply-license-preview" />
                      )}
                    </div>
                    <input
                      ref={licenseRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      className="hidden"
                      onChange={(e) => handleLicense(e.target.files?.[0])}
                      data-testid="apply-license-input"
                    />
                    {licenseFile
                      ? <p className="text-[11px] text-amber-800/80 mt-1 truncate">{licenseFile.name}</p>
                      : existingLicenseUrl && <p className="text-[11px] text-amber-800/80 mt-1">License on file — upload to replace (optional).</p>}
                  </div>
                </div>
              </div>

              <div>
                <Label>Anniversary</Label>
                <Input type="date" value={form.anniversary} onChange={(e) => setForm({ ...form, anniversary: e.target.value })} data-testid="apply-anniversary" />
              </div>

              <div className="sm:col-span-2">
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything else you'd like us to know" data-testid="apply-notes" />
              </div>

              <LocationsSection
                value={form}
                onChange={(patch) => setForm({ ...form, ...patch })}
                label="Do you have multiple locations?"
              />
            </div>

            <Button type="submit" className="btn-brand w-full" disabled={submitting} data-testid="apply-submit-button">
              {submitting ? (updateMode ? "Saving…" : "Submitting…") : (updateMode ? "Update my profile" : "Submit application")}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
